import { useEffect, useMemo, useState } from "react";
import { FaFilter, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import SimpleDropdown, { SimpleDropdownOption } from "./SimpleDropdown";

export type Filter = {
  id: string;
  label: string;
  icon?: React.ReactNode;
};
export type FilterOption = {
  id: string;
  label: string;
};
export type SelectableFilterOption = Filter & {
  options: FilterOption[];
};
const ANY: FilterOption = { id: "any", label: "Alle" };
type FilterState = {
  [key: string]: FilterOption;
};

export type SortOption = {
  id: string;
  label: string;
};

type SortState = {
  sortBy: string;
  sortOrder: "asc" | "desc";
};

export type SortAndFilterableItem = {
  id: string;
  [key: string]: any;
};

export type SortAndFilterSectionProps = {
  selectableFilterOptions: SelectableFilterOption[];
  sortOptions: SortOption[];
  items: SortAndFilterableItem[];
  onChange: (filteredAndSortedItems: SortAndFilterableItem[]) => void;
};

const SortAndFilterSection: React.FC<SortAndFilterSectionProps> = ({
  selectableFilterOptions: selectableFilters,
  sortOptions,
  items,
  onChange,
}) => {
  // Memoize the initial filter state to prevent recreation on every render
  const initialFilterState: FilterState = {};
  selectableFilters.forEach((filter) => {
    initialFilterState[filter.id] = ANY;
  });
  const [filterState, setFilterState] =
    useState<FilterState>(initialFilterState);
  const [sortState, setSortState] = useState<SortState>({
    sortBy: sortOptions[0].id,
    sortOrder: "asc",
  });
  const [textSearch, setTextSearch] = useState<string>("");

  // Memoize the filtered and sorted items to prevent unnecessary recalculations
  const filteredAndSortedItems = useMemo(() => {
    let filteredItems = items.filter((item) => {
      // Apply filters
      for (const filterId in filterState) {
        if (filterState[filterId].id !== "any") {
          if (item[filterId] !== filterState[filterId].id) {
            return false;
          }
        }
      }

      // Apply text search (assuming items have a name or username property)
      if (textSearch) {
        const searchLower = textSearch.toLowerCase();
        const hasMatch = Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchLower),
        );
        if (!hasMatch) {
          return false;
        }
      }

      return true;
    });

    // Apply sorting
    if (sortState.sortBy) {
      filteredItems.sort((a, b) => {
        const aValue = a[sortState.sortBy];
        const bValue = b[sortState.sortBy];

        let comparison = 0;
        if (aValue < bValue) {
          comparison = -1;
        } else if (aValue > bValue) {
          comparison = 1;
        }

        return sortState.sortOrder === "desc" ? -comparison : comparison;
      });
    }

    return filteredItems;
  }, [filterState, sortState, textSearch, items]);

  useEffect(() => {
    onChange(filteredAndSortedItems);
  }, [filteredAndSortedItems, onChange]);

  const handleFilterSelection = (
    filterId: string,
    selectedOption: SimpleDropdownOption,
  ) => {
    setFilterState((prev) => ({
      ...prev,
      [filterId]: selectedOption,
    }));
  };

  const handleTextSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextSearch(e.target.value);
  };

  return (
    <div className="sort-and-filter-section">
      <div className="filter-search">
        <input
          type="text"
          placeholder="Søk på navn eller site..."
          value={textSearch}
          onChange={handleTextSearchChange}
        />
      </div>
      {selectableFilters.map((filter) => (
        <SimpleDropdown
          className="filter-dropdown"
          key={filter.id}
          options={[ANY, ...filter.options]}
          defaultValue={ANY}
          onChange={(selectedOption) =>
            handleFilterSelection(filter.id, selectedOption)
          }
        >
          {filter.icon || <FaFilter />}
          {filter.label}: <b>{filterState[filter.id].label}</b>
        </SimpleDropdown>
      ))}
      <SimpleDropdown
        className="sort-dropdown"
        options={sortOptions}
        defaultValue={
          sortOptions.find((option) => option.id === sortState.sortBy) ||
          sortOptions[0]
        }
        onChange={(selectedOption) =>
          setSortState((prev) => ({
            ...prev,
            sortBy: selectedOption.id,
          }))
        }
      >
        <button
          className="sort-order-button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setSortState((prev) => ({
              ...prev,
              sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
            }));
          }}
        >
          {sortState.sortOrder === "asc" ? (
            <FaSortAmountDown />
          ) : (
            <FaSortAmountUp />
          )}
        </button>
        <b>
          {sortOptions.find((option) => option.id === sortState.sortBy)?.label}
        </b>
      </SimpleDropdown>
    </div>
  );
};

export default SortAndFilterSection;
